/**
 * NutritionCitation — small inline source tag rendered under every nutrition
 * recommendation (highlight, watch-out, alternative, goal-support, summary).
 *
 * Apple 1.4.1: every health/nutrition recommendation must cite its
 * authoritative source. The analyzer prompt now returns a `source` field on
 * each item; this component renders the tag consistently.
 *
 * Sources are always chosen from a fixed vocabulary — USDA, NIH, WHO, AND,
 * PubMed — so citations look uniform across screens.
 */
import { BookOpen } from "lucide-react";

const SOURCE_MAP = {
  USDA:   { full: "USDA FoodData Central" },
  NIH:    { full: "NIH Dietary Reference Intakes" },
  WHO:    { full: "WHO Healthy Diet" },
  AND:    { full: "Academy of Nutrition and Dietetics" },
  PubMed: { full: "PubMed" },
};

/**
 * Extract a source key from a value that may be a plain string, an object
 * with `.source`, or missing entirely.
 */
export function citationOf(item, fallback = "AND") {
  if (!item) return fallback;
  if (typeof item === "string") return fallback;
  const raw = (item.source || "").trim();
  if (SOURCE_MAP[raw]) return raw;
  // Common variants the model might emit
  const m = raw.toLowerCase();
  if (m.includes("usda")) return "USDA";
  if (m.includes("nih"))  return "NIH";
  if (m.includes("who"))  return "WHO";
  if (m.includes("acad") || m.includes("dietetics") || m === "and") return "AND";
  if (m.includes("pubmed")) return "PubMed";
  return fallback;
}

/**
 * `textOf` normalises highlight / watch_out items which can be either the
 * legacy string shape ("Provides moderate protein…") or the new object shape
 * ({text: "…", source: "NIH"}).
 */
export function textOf(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.text || item.reason || "";
}

export default function NutritionCitation({ source, onOpenSources, testId }) {
  const info = SOURCE_MAP[source] || SOURCE_MAP.AND;
  return (
    <button
      type="button"
      onClick={onOpenSources}
      data-testid={testId || `nutri-cite-${(source || "AND").toLowerCase()}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        marginTop: 6,
        padding: "3px 8px",
        borderRadius: 999,
        border: "1px solid rgba(178,138,62,0.28)",
        background: "rgba(178,138,62,0.08)",
        color: "var(--gold, #b28a3e)",
        fontSize: 10.5,
        letterSpacing: "0.04em",
        fontWeight: 500,
        cursor: onOpenSources ? "pointer" : "default",
      }}
      title={`Source: ${info.full} — tap to view all sources`}
    >
      <BookOpen size={10} strokeWidth={2} />
      Source: {info.full}
    </button>
  );
}
